import React, { useState, useCallback } from 'react';
import { CloudArrowUpIcon, DocumentIcon, PhotoIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import knowledgeService from '../../services/knowledgeService';

interface DocumentUploaderProps {
  onUploadSuccess: () => void;
}

const DocumentUploader: React.FC<DocumentUploaderProps> = ({ onUploadSuccess }) => {
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [category, setCategory] = useState('general');
  const [priority, setPriority] = useState(0);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      const file = files[0];
      if (validateFile(file)) {
        setSelectedFile(file);
      }
    }
  }, []);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      const file = files[0];
      if (validateFile(file)) {
        setSelectedFile(file);
      }
    }
  };

  const isImageFile = (file: File): boolean => {
    return file.type.startsWith('image/') || 
           ['.png', '.jpg', '.jpeg', '.gif', '.webp'].includes(
             file.name.toLowerCase().substring(file.name.lastIndexOf('.'))
           );
  };

  const validateFile = (file: File): boolean => {
    const allowedTypes = [
      // Documents
      'application/pdf',
      'text/plain',
      'text/markdown',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      // Images
      'image/png',
      'image/jpeg',
      'image/jpg',
      'image/gif',
      'image/webp'
    ];

    const allowedExtensions = ['.pdf', '.txt', '.md', '.docx', '.png', '.jpg', '.jpeg', '.gif', '.webp'];
    const fileExtension = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));

    if (!allowedTypes.includes(file.type) && !allowedExtensions.includes(fileExtension)) {
      toast.error('Please upload a PDF, DOCX, TXT, MD, or image file (PNG, JPG, GIF, WebP)');
      return false;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast.error('File size must be less than 10MB');
      return false;
    }

    return true;
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      toast.error('Please select a file to upload');
      return;
    }

    setIsUploading(true);
    const loadingToast = toast.loading('Uploading and processing document...');

    try {
      const response = await knowledgeService.uploadDocument(selectedFile, category, priority);
      
      toast.dismiss(loadingToast);
      toast.success(
        <div>
          <p className="font-semibold">Document uploaded successfully!</p>
          <p className="text-sm">
            Created {response.totalChunks} chunks with {response.extractedQAs} Q&A pairs
          </p>
        </div>,
        { duration: 5000 }
      );

      // Reset form
      setSelectedFile(null);
      setCategory('general');
      setPriority(0);
      
      // Trigger refresh of document list
      onUploadSuccess();
    } catch (error) {
      toast.dismiss(loadingToast);
      toast.error(error instanceof Error ? error.message : 'Failed to upload document');
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Upload Document</h3>
      
      {/* Drag and Drop Area */}
      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={`
          relative border-2 border-dashed rounded-lg p-8 text-center transition-colors
          ${isDragging ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:border-gray-400'}
          ${selectedFile ? 'bg-green-50 border-green-400' : ''}
        `}
      >
        <input
          type="file"
          id="file-upload"
          className="sr-only"
          accept=".pdf,.docx,.txt,.md,.png,.jpg,.jpeg,.gif,.webp,application/pdf,text/plain,text/markdown,application/vnd.openxmlformats-officedocument.wordprocessingml.document,image/png,image/jpeg,image/gif,image/webp"
          onChange={handleFileSelect}
          disabled={isUploading}
        />
        
        <label
          htmlFor="file-upload"
          className="cursor-pointer"
        >
          {selectedFile ? (
            <div className="flex flex-col items-center">
              {isImageFile(selectedFile) ? (
                <PhotoIcon className="h-12 w-12 text-blue-500 mb-3" />
              ) : (
                <DocumentIcon className="h-12 w-12 text-green-500 mb-3" />
              )}
              <p className="text-sm font-medium text-gray-900">{selectedFile.name}</p>
              <p className="text-xs text-gray-500 mt-1">
                {(selectedFile.size / 1024).toFixed(1)} KB
                {isImageFile(selectedFile) && (
                  <span className="ml-2 text-blue-600">• Image will be processed with AI</span>
                )}
              </p>
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  setSelectedFile(null);
                }}
                className="mt-2 text-sm text-red-600 hover:text-red-700"
              >
                Remove file
              </button>
            </div>
          ) : (
            <div className="flex flex-col items-center">
              <CloudArrowUpIcon className="h-12 w-12 text-gray-400 mb-3" />
              <p className="text-sm font-medium text-gray-900">
                Drop your document here, or{' '}
                <span className="text-blue-600">browse</span>
              </p>
              <p className="text-xs text-gray-500 mt-1">
                PDF, DOCX, TXT, MD, or image files up to 10MB
              </p>
            </div>
          )}
        </label>
      </div>

      {/* Category and Priority */}
      <div className="mt-4 grid grid-cols-2 gap-4">
        <div>
          <label htmlFor="category" className="block text-sm font-medium text-gray-700">
            Category
          </label>
          <select
            id="category"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            disabled={isUploading}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
          >
            <option value="general">General</option>
            <option value="admissions">Admissions</option>
            <option value="programs">Programs</option>
            <option value="tuition">Tuition & Fees</option>
            <option value="special-needs">Special Needs</option>
            <option value="policies">Policies</option>
          </select>
        </div>

        <div>
          <label htmlFor="priority" className="block text-sm font-medium text-gray-700">
            Priority
          </label>
          <input
            type="number"
            id="priority"
            min="0"
            max="10"
            value={priority}
            onChange={(e) => setPriority(parseInt(e.target.value) || 0)}
            disabled={isUploading}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
          />
        </div>
      </div>

      {/* Upload Button */}
      <button
        onClick={handleUpload}
        disabled={!selectedFile || isUploading}
        className={`
          mt-4 w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white
          ${!selectedFile || isUploading
            ? 'bg-gray-400 cursor-not-allowed'
            : 'bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500'
          }
        `}
      >
        {isUploading ? 'Processing...' : 'Upload Document'}
      </button>
    </div>
  );
};

export default DocumentUploader;